import { Callback } from '@csp/csp-common-model';
import { useCallback } from 'react';
import { To, useLocation, useNavigate } from 'react-router-dom';
import { UrlUtil } from '@csp/web-common';
import { CspPath } from '../../../CspPath';
import type { NavBackContext } from '../model/NavBackContext';
import type { NavigateState } from '../model/NavigateState';

type NavBackPath = CspPath | string;

type UseContextualNavBackHookResult = {
  contextualNavBackPath: CspPath;
  handleBackButtonClick: Callback;
  getNavigationContext: <T extends { [key: string]: unknown }>(
    to: To,
    navBackPath?: NavBackPath,
    additionalState?: T,
  ) => NavBackContext<T>;
  navigateWithContextualNavBack: (
    to: To,
    navBackPath?: NavBackPath,
    additionalState?: { [key: string]: unknown },
  ) => void;
};

export const useContextualNavBack = (defaultNavBackPath?: NavBackPath): UseContextualNavBackHookResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const contextualNavBackPath = (location?.state as NavigateState)?.navBackPath || defaultNavBackPath;

  const getNavigationContext = useCallback(
    <T extends { [key: string]: unknown }>(
      to: To,
      navBackPath?: NavBackPath,
      additionalState?: T,
    ): NavBackContext<T> => {
      const navback = navBackPath ?? contextualNavBackPath;
      const navbackWithParams = UrlUtil.addQueryParamsToPath(navback, location?.search);

      return {
        to,
        options: {
          state: {
            navBackPath: navbackWithParams as CspPath,
            ...(additionalState as T),
          },
        },
      };
    },
    [contextualNavBackPath, location?.search],
  );

  const navigateWithContextualNavBack = useCallback(
    (to: To, navBackPath?: NavBackPath, additionalState?: { [key: string]: unknown }): void => {
      const { options } = getNavigationContext(to, navBackPath, additionalState);

      navigate(to, options);
    },
    [getNavigationContext, navigate],
  );

  const handleBackButtonClick = useCallback((): void => {
    navigate(contextualNavBackPath);
  }, [contextualNavBackPath, navigate]);

  return {
    contextualNavBackPath,
    handleBackButtonClick,
    getNavigationContext,
    navigateWithContextualNavBack,
  };
};
